/* eslint-disable no-param-reassign */
import React, { useCallback } from 'react';

import { LayoutChangeEvent, TouchableOpacity, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
    SharedValue,
    runOnJS,
    useAnimatedStyle,
    useDerivedValue,
    useSharedValue,
    withSpring,
    withTiming,
} from 'react-native-reanimated';

import { DragIcon, EyeCrossedIcon, EyeIcon } from 'assets/images';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { RBChannel } from 'store/railsBuilder/reducer.types';

import { ImageSize } from 'components/NagraImage/types';
import ChannelCard from 'components/Ribbon/components/ChannelCard/ChannelCard';

import { ChannelsContent } from '../ChannelLineupItem/ChannelLineupItem';
import styles, { SEPARATOR_HEIGHT } from './styles';

const {
    ACCESSIBILITY_MY_LIST_HIDE_CHANNEL,
    ACCESSIBILITY_MY_LIST_SHOW_CHANNEL,
} = LANGUAGE_KEYS.ACCESSIBILITY.SETTINGS.CHANNEL_LINEUP;

type DraggableItemProps = {
    item: ChannelsContent;
    index: number;
    draggedIndex: SharedValue<number | null>;
    offsetY: SharedValue<number>;
    moveItem: (fromIndex: number, toIndex: number) => void;
    updateItemVisibility: (index: number) => () => void;
};

const useDraggableItem = ({
    index,
    draggedIndex,
    offsetY,
    moveItem,
}: {
    index: number;
    draggedIndex: SharedValue<number | null>;
    offsetY: SharedValue<number>;
    moveItem: (fromIndex: number, toIndex: number) => void;
}) => {
    const itemHeight = useSharedValue(0);
    const startY = useSharedValue(0);

    const getUpdatedOffsetY = useCallback(
        (translationY: number) => {
            'worklet';

            return Math.max(
                -index! * (itemHeight.value + SEPARATOR_HEIGHT),
                translationY + startY.value,
            );
        },
        [index],
    );

    const movingDirection = useDerivedValue(() => {
        'worklet';

        if (draggedIndex.value === null) return 0;

        const initialDraggedY = draggedIndex.value * itemHeight.value;
        const currentY = index * itemHeight.value;

        if (
            initialDraggedY < currentY &&
            currentY < initialDraggedY + offsetY.value + itemHeight.value / 2
        ) {
            return -1;
        }

        if (
            initialDraggedY + offsetY.value - itemHeight.value / 2 <=
                currentY &&
            currentY < initialDraggedY
        ) {
            return 1;
        }

        return 0;
    });

    const nextIndexToInsertAt = useDerivedValue(() => {
        'worklet';

        if (draggedIndex.value === null) return index;

        const initialDraggedY = draggedIndex.value * itemHeight.value;

        return Math.round((initialDraggedY + offsetY.value) / itemHeight.value);
    });

    const panGesture = Gesture.Pan()
        .minDistance(5)
        .onBegin(() => {
            draggedIndex.value = index;
        })
        .onUpdate((e) => {
            offsetY.value = getUpdatedOffsetY(e.translationY);
        })
        .onEnd(() => {
            if (draggedIndex.value === null) return;

            const indexOffset = nextIndexToInsertAt.value - draggedIndex.value;

            const targetOffsetY =
                indexOffset * (itemHeight.value + SEPARATOR_HEIGHT);

            offsetY.value = withTiming(targetOffsetY, { duration: 0 }, () => {
                if (draggedIndex.value === null) return;

                runOnJS(moveItem)(
                    draggedIndex.value,
                    nextIndexToInsertAt.value,
                );
            });
        })
        .onFinalize(() => {});

    const translateY = useDerivedValue(() => {
        'worklet';

        if (draggedIndex.value === null) return 0;
        if (draggedIndex.value === index) return withSpring(offsetY.value);

        return withSpring(movingDirection.value * itemHeight.value);
    });

    const animatedStyle = useAnimatedStyle(() => {
        const isBeingDragged = draggedIndex.value === index;

        return {
            transform: [{ translateY: translateY.value }],
            zIndex: isBeingDragged ? 10 : 0,
        };
    });

    const onLayout = useCallback((event: LayoutChangeEvent) => {
        itemHeight.value = event.nativeEvent.layout.height;
    }, []);

    return {
        animatedStyle,
        onLayout,
        panGesture,
    };
};

const DraggableItem = ({
    item,
    index,
    draggedIndex,
    offsetY,
    moveItem,
    updateItemVisibility,
}: DraggableItemProps) => {
    const { t } = useTranslation();
    const { animatedStyle, onLayout, panGesture } = useDraggableItem({
        index,
        draggedIndex,
        offsetY,
        moveItem,
    });

    return (
        <Animated.View onLayout={onLayout} style={animatedStyle}>
            {item.hidden ? (
                <div />
            ) : (
                <View style={styles.container}>
                    <View tabIndex={-1} style={styles.icon}>
                        <GestureDetector gesture={panGesture}>
                            <DragIcon />
                        </GestureDetector>
                    </View>
                    <ChannelCard
                        item={item as unknown as Partial<RBChannel>}
                        style={styles.channelContainer}
                        logoStyle={styles.logo}
                        width={ImageSize.SETTINGS_LIST_CHANNEL_LOGO.width}
                        height={ImageSize.SETTINGS_LIST_CHANNEL_LOGO.height}
                        isTitleVisible
                        isOnSettingsPage
                    />

                    <TouchableOpacity
                        onPress={updateItemVisibility(index)}
                        style={styles.icon}
                        accessibilityRole="switch"
                        aria-checked={item.visible}
                        accessibilityLabel={
                            item.visible
                                ? t(ACCESSIBILITY_MY_LIST_HIDE_CHANNEL)
                                : t(ACCESSIBILITY_MY_LIST_SHOW_CHANNEL)
                        }
                    >
                        {item.visible ? <EyeIcon /> : <EyeCrossedIcon />}
                    </TouchableOpacity>
                </View>
            )}
        </Animated.View>
    );
};

export default DraggableItem;
